import { useEffect } from 'react'
import CsrfTokenTag from '../../../../lib/CsrfTokenTag'
import ErrorMessage from '../ErrorMessage'
import CommentField from './fields/CommentField'
import PriceField from './fields/PriceField'
import ProductNameField from './fields/ProductNameField'
import ShopSearchField from './fields/ShopSearchField'
import UserInfoDisplay from './fields/UserInfoDisplay'
import { useImageProcessing } from './hooks/useImageProcessing'
import { usePostForm } from './hooks/usePostForm'
import { useShopSearch } from './hooks/useShopSearch'
import PostPreview from './PostPreview'
import PostSuccess from './PostSuccess'
import { createPostWithOnlySuccessStatus } from '../../../api'
import { colors } from '../../../colors'
import type { BargainUserInfo, Shop, Theme } from '../../../types'

interface PostFormProps {
  theme: Theme
  bargain_user_info: BargainUserInfo
  myarea_shops: Shop[]
}

const styles: Record<string, React.CSSProperties> = {
  postForm: {
    maxWidth: '600px',
    margin: '0 auto',
    backgroundColor: colors.white,
    overflow: 'hidden',
  },
  headerSection: {
    backgroundColor: colors['keijiban-coral-bg'],
    padding: '20px 16px',
    textAlign: 'center',
  },
  campaignPeriod: {
    fontSize: '14px',
    margin: '0 0 10px 0',
    color: colors['keijiban-coral'],
    fontWeight: '500',
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '0 0 6px 0',
    color: colors['keijiban-brown'],
  },
  formDescription: {
    fontSize: '14px',
    fontWeight: '600',
    margin: '0',
    color: colors['keijiban-brown'],
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  formContent: {
    padding: '24px 16px',
  },
  formActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '24px',
  },
  submitButton: {
    padding: '16px 24px',
    backgroundColor: 'rgba(0,0,0,0.12)',
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '100%',
  },
  submitButtonActive: {
    backgroundColor: colors.primary,
  },
  backButton: {
    padding: '16px 24px',
    backgroundColor: 'transparent',
    color: colors['text-secondary'],
    border: `1px solid ${colors['border-light']}`,
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  previewTitleBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
  },
  previewBoxConfirmText: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors['text-default'],
    marginBottom: '8px',
  },
  previewTitle: {
    fontSize: '14px',
    fontWeight: 300,
    color: colors['keijiban-brown'],
    margin: '0',
  },
  disabledButton: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
}

const PostForm: React.FC<PostFormProps> = ({ theme, bargain_user_info, myarea_shops }) => {
  // 画像処理
  const {
    images,
    processedImages,
    imagePreviews,
    loading: imageLoading,
    handleImageChange,
    handleRemoveImage,
  } = useImageProcessing()

  // フォーム状態管理
  const {
    formData,
    isPreviewMode,
    isPostCompleted,
    loading,
    error,
    isFormValid,
    setLoading,
    setError,
    updateFormData,
    handleProductNameChange,
    handleCommentChange,
    handlePriceChange,
    handlePosterNameChange,
    handleShowPreview,
    handleBackToEdit,
    handlePostSuccess,
    validateAndSetError,
  } = usePostForm(bargain_user_info, images)

  // ショップ検索
  const {
    shopId,
    shopName,
    chainName,
    chainLogoUrl,
    searchQuery,
    searchResults,
    isSearching,
    isShopInputFocused,
    handleShopSearchChange,
    handleShopInputFocus,
    handleShopInputBlur,
    handleShopSelect,
  } = useShopSearch(myarea_shops)

  // ショップ選択時にフォームデータを更新
  useEffect(() => {
    updateFormData({ shopId })
  }, [shopId, updateFormData])

  const labelWidth = 90

  const hasInput =
    !!formData.shopId ||
    !!formData.productName ||
    !!formData.comment ||
    !!formData.price ||
    !!formData.posterName ||
    images.length > 0

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasInput && !isPostCompleted) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasInput, isPostCompleted])

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isPreviewMode) {
      handleShowPreview()
      return
    }

    if (!validateAndSetError()) return

    try {
      setLoading(true)
      const formDataToSubmit = new FormData()

      // フォームデータの構築
      formDataToSubmit.append('post[bargain_message_board_theme_id]', theme.id.toString())
      formDataToSubmit.append('post[bargain_shop_id]', shopId?.toString() || '')
      formDataToSubmit.append('post[product_name]', formData.productName)
      formDataToSubmit.append('post[content]', formData.comment)

      // ユーザー名がない場合は入力されたユーザー名を使用
      if (!bargain_user_info.name && formData.posterName) {
        formDataToSubmit.append('post[poster_name]', formData.posterName)
      }

      if (formData.price) {
        formDataToSubmit.append('post[purchased_price]', formData.price)
      }

      // 処理済み画像の追加（jpeg形式に変換済み）
      processedImages.forEach((image) => {
        formDataToSubmit.append(`images[]`, image)
      })

      const result = await createPostWithOnlySuccessStatus(formDataToSubmit)
      if (result) {
        handlePostSuccess()
      } else {
        handleBackToEdit()
      }
    } catch {
      setError('投稿の作成に失敗しました。もう一度お試しください。')
      handleBackToEdit()
    } finally {
      setLoading(false)
    }
  }

  const handleImageChangeWithError = async (files: FileList) => {
    try {
      await handleImageChange(files)
    } catch (error) {
      setError(error instanceof Error ? error.message : '画像の処理中にエラーが発生しました。')
    }
  }

  const handleBack = () => {
    window.location.href = '/webview/message_board/posts'
  }

  return (
    <div style={styles.postForm}>
      {/* ヘッダーセクション */}
      {!isPreviewMode && (
        <div style={styles.headerSection}>
          <div style={styles.campaignPeriod}>
            {new Date(theme.entry_end_datetime).toLocaleDateString('ja-JP')}
            まで募集中
          </div>
          <h2 style={styles.formTitle}>「{theme.title}」</h2>
          <p style={styles.formDescription}>{theme.description}</p>
        </div>
      )}

      {/* フォームコンテンツ */}
      <div style={styles.formContent}>
        {error && <ErrorMessage message={error} />}

        <form onSubmit={handleSubmit} autoComplete="off">
          {!isPreviewMode ? (
            <>
              <ProductNameField
                value={formData.productName}
                onChange={handleProductNameChange}
                labelWidth={labelWidth}
              />

              <CommentField
                value={formData.comment}
                onChange={handleCommentChange}
                images={processedImages}
                imagePreviews={imagePreviews}
                loading={imageLoading}
                onImageChange={handleImageChangeWithError}
                onRemoveImage={handleRemoveImage}
                bargain_user_info={bargain_user_info}
                labelWidth={labelWidth}
              />

              <ShopSearchField
                searchQuery={searchQuery}
                searchResults={searchResults}
                isSearching={isSearching}
                isShopInputFocused={isShopInputFocused}
                onSearchChange={handleShopSearchChange}
                onInputFocus={handleShopInputFocus}
                onInputBlur={handleShopInputBlur}
                onShopSelect={handleShopSelect}
                labelWidth={labelWidth}
              />

              <PriceField
                value={formData.price}
                onChange={handlePriceChange}
                labelWidth={labelWidth}
              />

              <UserInfoDisplay
                bargain_user_info={bargain_user_info}
                posterName={formData.posterName}
                onPosterNameChange={handlePosterNameChange}
                labelWidth={labelWidth}
              />

              {/* アクションボタン */}
              <div style={styles.formActions}>
                <button
                  type="button"
                  onClick={handleShowPreview}
                  style={{
                    ...styles.submitButton,
                    ...(isFormValid ? styles.submitButtonActive : {}),
                    ...(loading ? styles.disabledButton : {}),
                  }}
                  disabled={loading}
                >
                  確認する
                </button>
                <button type="button" onClick={handleBack} style={styles.backButton}>
                  口コミ一覧
                </button>
              </div>
            </>
          ) : isPostCompleted ? (
            <PostSuccess />
          ) : (
            <>
              <div style={styles.previewTitleBox}>
                <h2 style={styles.previewBoxConfirmText}>この内容で投稿しますか？</h2>
                <div style={styles.previewTitle}>お題：「{theme.title}」</div>
              </div>

              <PostPreview
                formData={formData}
                shopName={shopName}
                chainName={chainName}
                chainLogoUrl={chainLogoUrl}
                bargain_user_info={bargain_user_info}
                imagePreviews={imagePreviews}
                loading={loading}
                onBackToEdit={handleBackToEdit}
                onSubmit={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
              />
            </>
          )}

          {/* CSRF対策 */}
          <CsrfTokenTag />
        </form>
      </div>
    </div>
  )
}

export default PostForm
